import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, unref as _unref } from "vue"

const _hoisted_1 = { style: {"white-space":"nowrap","overflow":"hidden","text-overflow":"ellipsis"} }

import { ref, watch, onMounted, computed } from 'vue';
import { useProductsStore } from '@/store/products';
import { useClientsStore } from '@/store/clients';
import { debounce } from 'lodash';


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductSelector',
  props: {
  modelValue: { type: Object, required: true },
  clientId: { type: [String, Number], required: false },
  subject: { type: Object, required: false }
},
  emits: ['update:modelValue', 'validation'],
  setup(__props, { emit: __emit }) {

const cachedSearches = new Map<string, any[]>();

const emit = __emit;
const props = __props;

const clientStore = useClientsStore();
const productsStore = useProductsStore();

const localValue = ref({ ...props.modelValue });
const productOptions = ref<any[]>([]);
const productableOptions = ref<any[]>([]);
const loadingProducts = ref(false);

const validate = () => {
  const valid =
    !!localValue.value.item_id &&
    (!!localValue.value.unit_price || localValue.value.unit_price === 0) &&
    (productableOptions.value.length === 0 || !!localValue.value.productable_id);

  emit('validation', valid);
};

watch(() => props.modelValue, (val) => {
  localValue.value = { ...val };
  hydrateSelectedProduct(val);
}, { deep: true });

watch(() => localValue.value, (val) => {
  emit('update:modelValue', val);
  validate();
}, { deep: true });

const onProductSelect = async (productId: number) => {
  const selected = productOptions.value.find(p => p.value === productId);
  if (!selected) return;

  localValue.value.item_id = productId;

  const productables = selected.raw?.attributes?.productable || [];

  // LIMPIA siempre antes de modificar
  localValue.value.productable_id = null;
  localValue.value.productables = [];

  if (productables.length > 0) {
    productableOptions.value = productables.map((p: any) => ({
      value: p.id,
      label: `${p.attributes.sku || ''} | ${p.attributes.address || ''}`.trim(),
      raw: p
    }));
    localValue.value.productables = productables;
  } else {
    productableOptions.value = [];
    // Settea directos para producto plano
    localValue.value.productable_id = productId;
    localValue.value.productable_type = selected.raw?.attributes?.productable_type;
    await fetchPrice({
      productId,
      productType: selected.raw?.attributes?.productable_type
    });
  }
};


const onProductableSelect = async (productableId: number) => {
  const product = localValue.value.productables?.find((p: any) => p.id === productableId);
  if (!product) return;

  localValue.value.productable_id = productableId;
  localValue.value.productable_type = product.attributes.productable_type;

  await fetchPrice({
    productId: productableId,
    productType: product.attributes.productable_type,
  });
};

const suggestedPrice = ref<number|null>(null);

const fetchPrice = async ({
  productId,
  productType
}: {
  productId: number;
  productType: string;
}) => {

  const subject = props.subject;
  const subjectId = subject?.id;
  const clientTypeId = subject?.attributes?.client_type?.id;

  if (!subjectId || !clientTypeId || !productId || !productType) return;

  try {
    const price = await productsStore.fetchSuggestedPrice(
      'filter[client_type_id]=' + clientTypeId +
      '&filter[product_type]=' + productType +
      '&filter[product_id]=' + productId
    );
    localValue.value.unit_price = price;
    suggestedPrice.value = price; // <-- Aquí guardas el precio sugerido
  } catch (err) {
    console.error('Error fetching price:', err);
    suggestedPrice.value = null;
  }
};

const requiereAutorizacion = computed(() =>
  suggestedPrice.value != null &&
  Number(localValue.value.unit_price) < Number(suggestedPrice.value)
);

const onMenuFocus = () => {
  // Solo busca si NO hay opciones
  if (!productOptions.value.length) {
    onSearch('');
  }
};

const onSearch = debounce(async (search: string) => {
  if (typeof search !== 'string') return; // Solo busca si es texto
  if (!search && productOptions.value.length) return; // Si ya hay opciones, no busques otra vez
  //if (!search || search.length < 2) return;
  loadingProducts.value = true;

  if (cachedSearches.has(search)) {
    productOptions.value = cachedSearches.get(search)!;
    loadingProducts.value = false;
    return;
  }

  try {
    const { data } = await productsStore.fetchProducts(typeof search === 'string'?{ 'filter[description]': search }:'');
    const mapped = mapProducts(data);
    cachedSearches.set(search, mapped);
    productOptions.value = mapped;
  } catch (error) {
    console.error('Error searching products:', error);
  } finally {
    loadingProducts.value = false;
  }
}, 300);

const mapProducts = (data: any[]) =>
  data.map(p => ({
    value: p.id,
    label: p.attributes.description,
    raw: p
  }));

const hydrateSelectedProduct = (value: any) => {
  const { item_id, productables, productable_type, product_description, product_organization } = value || {};

  // Si es productable y tiene product base
  if (productable_type !== 'Product' && productables?.length) {
    const product = productables[0]?.attributes?.product;
    if (product) {
      productOptions.value = [{
        value: item_id,
        label: product.attributes?.description || 'Producto',
        raw: product
      }];
    }
    productableOptions.value = productables.map((p: any) => ({
      value: p.id,
      label: `${p.attributes.sku || ''} | ${p.attributes.address || ''}`.trim(),
      raw: p
    }));
  }

  // **Si es producto directo (tipo Product plano, sin productables)**
  if (productable_type === 'Product' && item_id) {
    // **Nuevo: Si productOptions está vacío, lo llenas con el item**
    if (!productOptions.value.length) {
      productOptions.value = [{
        value: item_id,
        label: product_description || 'Producto',
        raw: { id: item_id, attributes: { description: product_description, organization: product_organization } }
      }];
    }
  }
};

onMounted(() => {
  hydrateSelectedProduct(localValue.value);
});


watch(productableOptions, (opts) => {
  if (!opts.length) {
    localValue.value.productable_id = null;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_text_field = _resolveComponent("v-text-field")!

  return (localValue.value)
    ? (_openBlock(), _createBlock(_component_v_row, {
        key: 0,
        class: "ma-0",
        dense: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, {
            cols: productableOptions.value.length ? 4 : 9,
            class: "py-0"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_autocomplete, {
                modelValue: localValue.value.item_id,
                "onUpdate:modelValue": [
                  _cache[0] || (_cache[0] = ($event: any) => ((localValue.value.item_id) = $event)),
                  onProductSelect
                ],
                items: productOptions.value,
                loading: loadingProducts.value,
                "item-title": "label",
                "item-value": "value",
                label: "Servicio",
                density: "compact",
                "onUpdate:search": _unref(onSearch),
                onFocus: onMenuFocus,
                chips: "",
                variant: "solo-filled"
              }, {
                chip: _withCtx(({ props, item }) => [
                  _createElementVNode("div", _mergeProps(props, { style: {"display":"flex","align-items":"center","gap":"4px","padding":"0 6px","min-width":"0"} }), [
                    _createElementVNode("span", _hoisted_1, _toDisplayString(productOptions.value.find(product => product.value == item.value)?.raw?.attributes?.description || item.label), 1),
                    _createVNode(_component_v_chip, {
                      size: "x-small",
                      color: productOptions.value.find(product => product.value == item.value)?.raw?.attributes?.organization?.color,
                      label: "",
                      style: {"font-size":"10px","height":"15px"},
                      class: "ml-2"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(productOptions.value.find(product => product.value == item.value)?.raw?.attributes?.organization?.name), 1)
                      ]),
                      _: 2
                    }, 1032, ["color"]),
                    (Number(localValue.value.unit_price) === 0)
                      ? (_openBlock(), _createBlock(_component_v_chip, {
                          key: 0,
                          size: "x-small",
                          color: "green",
                          label: "",
                          style: {"font-size":"10px","height":"15px"},
                          class: "ml-2"
                        }, {
                          default: _withCtx(() => _cache[3] || (_cache[3] = [
                            _createTextVNode("Cortesía")
                          ])),
                          _: 1,
                          __: [3]
                        }))
                      : _createCommentVNode("", true)
                  ], 16)
                ]),
                item: _withCtx(({ item, props }) => [
                  _createVNode(_component_v_row, _mergeProps({ class: "ma-0 pa-4" }, props, { style: {"cursor":"pointer"} }), {
                    default: _withCtx(() => [
                      _createVNode(_component_v_list_item_title, null, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(item?.raw?.raw?.attributes?.description || item?.title || item), 1)
                        ]),
                        _: 2
                      }, 1024),
                      _createVNode(_component_v_spacer),
                      (item?.raw?.raw?.attributes?.organization != null)
                        ? (_openBlock(), _createBlock(_component_v_chip, {
                            key: 0,
                            size: "x-small"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(item?.raw?.raw?.attributes?.organization?.name || ''), 1)
                            ]),
                            _: 2
                          }, 1024))
                        : _createCommentVNode("", true)
                    ]),
                    _: 2
                  }, 1040)
                ]),
                _: 1
              }, 8, ["modelValue", "items", "loading", "onUpdate:search"])
            ]),
            _: 1
          }, 8, ["cols"]),
          (productableOptions.value.length)
            ? (_openBlock(), _createBlock(_component_v_col, {
                key: 0,
                cols: 5,
                class: "py-0"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_autocomplete, {
                    variant: "solo-filled",
                    modelValue: localValue.value.productable_id,
                    "onUpdate:modelValue": [
                      _cache[1] || (_cache[1] = ($event: any) => ((localValue.value.productable_id) = $event)),
                      onProductableSelect
                    ],
                    items: productableOptions.value,
                    "item-title": "label",
                    "item-value": "value",
                    label: "Seleccionar",
                    density: "compact"
                  }, null, 8, ["modelValue", "items"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_component_v_col, {
            cols: 3,
            class: "py-0"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: localValue.value.unit_price,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localValue.value.unit_price) = $event)),
                prefix: "$",
                type: "number",
                label: "Precio",
                density: "compact",
                hint: requiereAutorizacion.value ? `Requiere autorización` : [],
                variant: "solo-filled"
              }, null, 8, ["modelValue", "hint"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})